import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { configureApiClient } from '@/api/client'
import { oidcConfig, type OidcConfig } from '@/lib/env'

import { AuthContext, type AuthContextValue, type AuthStatus } from './auth-context'
import {
  AuthError,
  beginLogin,
  clearTokens,
  decodeIdToken,
  endSession,
  isExpired,
  loadTokens,
  REFRESH_SKEW_MS,
  refreshTokens,
  saveTokens,
  type IdTokenClaims,
  type TokenSet,
} from './oidc'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const config: OidcConfig | undefined = useMemo(() => oidcConfig(), [])

  const tokensRef = useRef<TokenSet | undefined>(undefined)
  const refreshInFlight = useRef<Promise<TokenSet | undefined> | undefined>(undefined)
  const refreshTimer = useRef<number | undefined>(undefined)

  const [status, setStatus] = useState<AuthStatus>('initialising')
  const [claims, setClaims] = useState<IdTokenClaims | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimer.current !== undefined) {
      window.clearTimeout(refreshTimer.current)
      refreshTimer.current = undefined
    }
  }, [])

  const signOutLocally = useCallback(() => {
    clearRefreshTimer()
    tokensRef.current = undefined
    clearTokens()
    setClaims(undefined)
    setStatus('unauthenticated')
  }, [clearRefreshTimer])

  /** Refresh, de-duplicated across concurrent callers. */
  const doRefresh = useCallback(async (): Promise<TokenSet | undefined> => {
    const refreshToken = tokensRef.current?.refresh_token
    if (config === undefined || refreshToken === undefined) {
      signOutLocally()
      return undefined
    }

    const existing = refreshInFlight.current
    if (existing !== undefined) return existing

    const attempt = (async (): Promise<TokenSet | undefined> => {
      try {
        const next = await refreshTokens(config, refreshToken)
        tokensRef.current = next
        saveTokens(next)
        setClaims((previous) => decodeIdToken(next.id_token) ?? previous)
        setStatus('authenticated')
        return next
      } catch {
        signOutLocally()
        return undefined
      } finally {
        refreshInFlight.current = undefined
      }
    })()

    refreshInFlight.current = attempt
    return attempt
  }, [config, signOutLocally])

  const scheduleRefresh = useCallback(
    (tokens: TokenSet) => {
      clearRefreshTimer()
      if (tokens.refresh_token === undefined) return
      const delay = Math.max(tokens.expires_at - Date.now() - REFRESH_SKEW_MS, 5_000)
      refreshTimer.current = window.setTimeout(() => {
        void doRefresh()
      }, delay)
    },
    [clearRefreshTimer, doRefresh],
  )

  const acceptTokens = useCallback(
    (tokens: TokenSet) => {
      tokensRef.current = tokens
      saveTokens(tokens)
      setClaims(decodeIdToken(tokens.id_token))
      setError(undefined)
      setStatus('authenticated')
      scheduleRefresh(tokens)
    },
    [scheduleRefresh],
  )

  const getAccessToken = useCallback(
    async (forceRefresh: boolean): Promise<string | undefined> => {
      const current = tokensRef.current
      if (current === undefined) return undefined
      if (!forceRefresh && !isExpired(current)) return current.access_token
      const next = await doRefresh()
      return next?.access_token
    },
    [doRefresh],
  )

  const login = useCallback(
    (returnTo?: string) => {
      if (config === undefined) {
        setError(
          'Sign-in is not configured. Set VITE_KEYCLOAK_ISSUER and VITE_KEYCLOAK_CLIENT_ID.',
        )
        setStatus('error')
        return
      }
      const target = returnTo ?? `${window.location.pathname}${window.location.search}`
      beginLogin(config, { returnTo: target }).catch((cause: unknown) => {
        setError(cause instanceof AuthError ? cause.message : 'Could not start sign-in.')
        setStatus('error')
      })
    },
    [config],
  )

  /**
   * Sign out for real: end the Keycloak session, not just this tab's copy of it.
   *
   * DO NOT SET `status` TO `unauthenticated` HERE. That is not tidiness, it is the bug this
   * function was written wrong for once already. `endSession` cannot redirect until it has
   * resolved the end-session endpoint, and that await — even served from the cached discovery
   * document — defers to a microtask. Flipping the status first let React re-render in that gap,
   * `RequireAuth` saw `unauthenticated` on an `/app/**` route and started a LOGIN, and Keycloak,
   * whose SSO cookie was still perfectly valid, signed the user straight back in. The logout
   * redirect never ran because the page had already left. It was a race the guard always won.
   *
   * The guard's behaviour is right for every other route to `unauthenticated` — a lapsed session
   * SHOULD send you to sign in. So the fix is not to teach it about logging out; it is to stop
   * announcing a state we are in the middle of navigating away from. Nothing needs to re-render:
   * this page is leaving, one way or the other, on every path below.
   */
  const logout = useCallback(() => {
    const idToken = tokensRef.current?.id_token
    clearRefreshTimer()
    tokensRef.current = undefined

    if (config === undefined) {
      // No IdP to end a session with, so there is no redirect to lose the race to and the local
      // state IS the whole sign-out. Safe to announce here, and necessary.
      clearTokens()
      setClaims(undefined)
      setStatus('unauthenticated')
      window.location.assign('/')
      return
    }

    // Clears the tokens, then navigates -- to the end-session endpoint, or to the post-logout URI
    // if discovery fails. Either way this document is replaced, so the React state it leaves
    // behind never renders.
    void endSession(config, idToken)
  }, [clearRefreshTimer, config])

  /**
   * Bootstrap: adopt any token set left in this tab's session storage.
   * Deliberately idempotent — StrictMode double-invokes it, and `doRefresh`
   * de-duplicates concurrent refreshes, so a second pass is a no-op.
   */
  useEffect(() => {
    if (config === undefined) {
      setStatus('unauthenticated')
      return
    }

    const stored = loadTokens()
    if (stored === undefined) {
      setStatus('unauthenticated')
      return
    }

    tokensRef.current = stored
    setClaims(decodeIdToken(stored.id_token))

    if (!isExpired(stored)) {
      setStatus('authenticated')
      scheduleRefresh(stored)
      return
    }

    if (stored.refresh_token === undefined) {
      signOutLocally()
      return
    }

    void (async () => {
      const next = await doRefresh()
      if (next !== undefined) scheduleRefresh(next)
    })()
  }, [config, doRefresh, scheduleRefresh, signOutLocally])

  useEffect(() => clearRefreshTimer, [clearRefreshTimer])

  /* Hand the API client its token accessor + 401 escalation path. */
  useEffect(() => {
    configureApiClient({
      getAccessToken,
      onUnauthorized: () => {
        signOutLocally()
      },
    })
  }, [getAccessToken, signOutLocally])

  const value: AuthContextValue = useMemo(
    () => ({
      status,
      configured: config !== undefined,
      claims,
      error,
      login,
      logout,
      getAccessToken,
      acceptTokens,
    }),
    [acceptTokens, claims, config, error, getAccessToken, login, logout, status],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
